import PayloadRegister from './PayloadRegister';
import fetchAccessToken from './fetchAccessToken';
import fetchAppToken from './fetchAppToken';

/**
 * Available methods
 * @typedef {'POST'|'PATCH'|'PUT'|'DELETE'|'GET'} Method
 */

/**
 * Body Options
 * @typedef {object} BodyOptions
 * @property {boolean} allowEmpty
 */

/**
 * Available Options for Backend Fetch.
 * @typedef {object} Options
 * @property {Method} method
 * @property {object} headers
 * @property {object?} body
 * @property {BodyOptions?} bodyOptions
 */

/**
 * @typedef {object} Response
 * @property {object} headers
 * @property {number} status
 * @property {boolean} ok
 * @property {object} data
 */

/**
 * Simplifiying fetching data from backend
 *
 * Lots of logic taken from Azure dataloader v2 mappedAzureFetch
 * @param {string} url
 * @param {Options} options
 * @returns {Promise<Response>}
 */
const backendFetch = async (url, options) => {
  if (!url) throw new Error('Expected URL to be provided!');

  const requestHeaders = options?.headers || {};

  let token = '';
  if (PayloadRegister.isProduction()) {
    token = await fetchAccessToken();
    if (!token) throw new Error('Expected token to be provided!');
  }

  if (PayloadRegister.isProduction()) {
    const appToken = await fetchAppToken();
    requestHeaders.Authorization = `JWT ${appToken}`;
  }

  const { method = 'GET', body, bodyOptions } = options;

  let requestBody = null;

  switch (method) {
    case 'POST':
    case 'PATCH':
    case 'PUT': {
      if (typeof body !== 'object') {
        throw new Error('Expected body to be of type Object!');
      }
      if (!bodyOptions.allowEmpty && Object.keys(body).length === 0) {
        throw new Error('Expected body to contain data, recieved empty!');
      }

      requestHeaders['Content-Type'] = 'application/json;charset-utf-8';
      requestBody = JSON.stringify(body);
      break;
    }
    case 'DELETE':
    case 'GET':
      break;
    default:
      throw new Error(`Unsupported CRUD method "${method}" caught!`);
  }

  const request = {
    body: requestBody,
    headers: requestHeaders,
    method: method,
  };

  return new Promise((resolve, reject) => {
    PayloadRegister.checkForPayload(url, request)
      .then(async (response) => {
        const { status, ok, headers } = response;

        if (!ok) throw response;
        if (status === 204) {
          resolve({
            status,
            ok,
            headers,
            data: {
              value: 'deleted',
            },
          });
        } else {
          resolve({
            status,
            ok,
            headers,
            data: await response.json(),
          });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Call got cancelled!');
          reject(error);
          return;
        }

        console.error(`Caught error when calling: ${url}`);
        console.error('With this request: ', request);
        console.error(error);
        reject(error);
      });
  });
};

export default backendFetch;
